/**
 * Dot-path utilities for editing THROUGH a `$bind`: rewriting alias paths
 * (`program.name`) to concrete data paths (`programs.0.name`) using the
 * repeat chain, and immutably setting a value at a concrete path.
 */

/** One active repeat scope, outermost first. */
export interface RepeatFrame {
  over: string;
  as: string;
  index: number;
}

/**
 * Rewrite a bind path against the active repeat frames. The innermost frame
 * whose alias matches the path's first segment wins; its `over` path is
 * itself concretized against the outer frames.
 */
export function concretizePath(path: string, frames: RepeatFrame[]): string {
  for (let i = frames.length - 1; i >= 0; i--) {
    const frame = frames[i]!;
    if (path === frame.as || path.startsWith(`${frame.as}.`)) {
      const rest = path === frame.as ? '' : path.slice(frame.as.length);
      const over = concretizePath(frame.over, frames.slice(0, i));
      return `${over}.${frame.index}${rest}`;
    }
  }
  return path;
}

/** Parse a rendered-instance key suffix (":0:2") into repeat indices. */
export function parseInstanceSuffix(suffix: string): number[] {
  if (!suffix) return [];
  return suffix
    .split(':')
    .filter((s) => s !== '')
    .map((s) => Number(s))
    .filter((n) => Number.isInteger(n) && n >= 0);
}

/**
 * Immutably set `value` at a dot path; numeric segments index arrays.
 * Returns a new root; intermediate containers are created as needed
 * (objects for name segments, arrays for numeric segments).
 */
export function setValueAtPath(
  root: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segments = path.split('.').filter((s) => s !== '');
  if (segments.length === 0) return root;

  const setIn = (container: unknown, depth: number): unknown => {
    const segment = segments[depth]!;
    const isIndex = /^\d+$/.test(segment);
    const last = depth === segments.length - 1;
    if (isIndex) {
      const arr = Array.isArray(container) ? [...container] : [];
      const i = Number(segment);
      arr[i] = last ? value : setIn(arr[i], depth + 1);
      return arr;
    }
    const obj =
      container !== null && typeof container === 'object' && !Array.isArray(container)
        ? { ...(container as Record<string, unknown>) }
        : {};
    obj[segment] = last ? value : setIn(obj[segment], depth + 1);
    return obj;
  };

  return setIn(root, 0) as Record<string, unknown>;
}

/** Immutably remove the value at a dot path (array segments splice). */
export function removeValueAtPath(
  root: Record<string, unknown>,
  path: string,
): Record<string, unknown> {
  const segments = path.split('.').filter((s) => s !== '');
  if (segments.length === 0) return root;

  const removeIn = (container: unknown, depth: number): unknown => {
    const segment = segments[depth]!;
    const last = depth === segments.length - 1;
    if (/^\d+$/.test(segment)) {
      if (!Array.isArray(container)) return container;
      const arr = [...container];
      const i = Number(segment);
      if (last) arr.splice(i, 1);
      else arr[i] = removeIn(arr[i], depth + 1);
      return arr;
    }
    if (container === null || typeof container !== 'object' || Array.isArray(container)) {
      return container;
    }
    const obj = { ...(container as Record<string, unknown>) };
    if (last) delete obj[segment];
    else obj[segment] = removeIn(obj[segment], depth + 1);
    return obj;
  };

  return removeIn(root, 0) as Record<string, unknown>;
}
